/**
 * StatusPageServiceManager — checkbox-based service picker that replaces
 * the old flat list + Add Service modal.
 *
 * Shows ALL checks grouped by Project → Group. Each check has a checkbox
 * to toggle it on/off the status page. Checked checks show an editable
 * display name inline. Search/filter and quota counter at the top.
 *
 * Toggles are optimistic: local state updates instantly, API syncs in
 * background, and reverts on error.
 */
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { api } from "@/lib/api";

// ─── Types ───────────────────────────────────────────────────────────────────

interface StatusPageService {
  id: string;
  tenant_id: string;
  check_uuid: string;
  display_name: string;
  sort_order: number;
  created_at: string;
}

interface ServiceQuota {
  current: number;
  limit: number;
}

interface CheckDefinition {
  uuid: string;
  name: string;
  project: string;
  group_name: string;
  type: string;
  url?: string;
  host?: string;
  port?: number;
  domain?: string;
  [key: string]: unknown;
}

interface StatusPageServiceManagerProps {
  checkDefs: CheckDefinition[];
  services: StatusPageService[];
  quota: ServiceQuota;
  onServicesChanged: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkTypeBadge(type: string) {
  const map: Record<string, string> = {
    http: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400",
    tcp: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400",
    dns: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400",
    ssh: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400",
    redis: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400",
    pgsql: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-400",
    mysql: "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-400",
    icmp: "bg-gray-100 text-gray-700 dark:bg-gray-900/40 dark:text-gray-400",
    ssl: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400",
    domain_expiry: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400",
    mongodb: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400",
  };
  return map[type] ?? "bg-muted text-muted-foreground";
}

function checkTarget(c: CheckDefinition): string {
  if (c.url) return c.url;
  if (c.host) return c.host + (c.port ? `:${c.port}` : "");
  if (c.domain) return c.domain;
  return "";
}

function formatQuota(current: number, limit: number): string {
  if (limit === -1) return `${current} / Unlimited`;
  return `${current} / ${limit}`;
}

/** Groups checks by project → group for the picker */
function groupChecks(
  checks: CheckDefinition[]
): Map<string, Map<string, CheckDefinition[]>> {
  const groups = new Map<string, Map<string, CheckDefinition[]>>();
  for (const c of checks) {
    const project = c.project || "(No Project)";
    const group = c.group_name || "(No Group)";
    if (!groups.has(project)) groups.set(project, new Map());
    const projectMap = groups.get(project)!;
    if (!projectMap.has(group)) projectMap.set(group, []);
    projectMap.get(group)!.push(c);
  }
  return groups;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function StatusPageServiceManager({
  checkDefs,
  services,
  quota,
  onServicesChanged,
}: StatusPageServiceManagerProps) {
  const [search, setSearch] = useState("");
  const [collapsedProjects, setCollapsedProjects] = useState<Set<string>>(
    new Set()
  );
  const [editingServiceId, setEditingServiceId] = useState<string | null>(null);
  const [editingDisplayName, setEditingDisplayName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [error, setError] = useState("");

  // ── Optimistic local state for services/quota ──
  // Start from props, update optimistically on toggle, sync back from props
  // when they change (e.g. after display name save or reorder).
  const [localServices, setLocalServices] = useState(services);
  const [localQuota, setLocalQuota] = useState(quota);
  const skipNextPropSync = useRef(false);

  useEffect(() => {
    // Sync from props when parent provides updated data
    // (after display name save, reorder, or initial load)
    if (!skipNextPropSync.current) {
      setLocalServices(services);
      setLocalQuota(quota);
    }
    skipNextPropSync.current = false;
  }, [services, quota]);

  // Build lookup: check_uuid → service (using local optimistic state)
  const serviceByUUID = useMemo(() => {
    const m = new Map<string, StatusPageService>();
    for (const svc of localServices) m.set(svc.check_uuid, svc);
    return m;
  }, [localServices]);

  // Filter checks by search
  const filtered = useMemo(() => {
    if (!search) return checkDefs;
    const q = search.toLowerCase();
    return checkDefs.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.project || "").toLowerCase().includes(q) ||
        (c.group_name || "").toLowerCase().includes(q) ||
        (c.type || "").toLowerCase().includes(q) ||
        checkTarget(c).toLowerCase().includes(q) ||
        (serviceByUUID.get(c.uuid)?.display_name || "")
          .toLowerCase()
          .includes(q)
    );
  }, [checkDefs, search, serviceByUUID]);

  const grouped = useMemo(() => groupChecks(filtered), [filtered]);

  const featureUnavailable = localQuota.limit === 0;
  const atQuotaLimit = localQuota.limit > 0 && localQuota.current >= localQuota.limit;

  const toggleProject = useCallback((project: string) => {
    setCollapsedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(project)) next.delete(project);
      else next.add(project);
      return next;
    });
  }, []);

  // ── Toggle check on/off status page (optimistic) ──

  const handleToggle = useCallback(
    async (check: CheckDefinition) => {
      const existing = serviceByUUID.get(check.uuid);
      setError("");

      // Snapshot for rollback
      const prevServices = localServices;
      const prevQuota = localQuota;

      if (existing) {
        // Optimistic remove
        setLocalServices((prev) => prev.filter((s) => s.id !== existing.id));
        setLocalQuota((prev) => ({ ...prev, current: Math.max(0, prev.current - 1) }));
      } else {
        // Optimistic add — create a temporary placeholder service
        const sortOrder =
          localServices.length > 0
            ? Math.max(...localServices.map((s) => s.sort_order)) + 1
            : 0;
        const tempService: StatusPageService = {
          id: `temp-${check.uuid}`,
          tenant_id: "",
          check_uuid: check.uuid,
          display_name: check.name || check.uuid,
          sort_order: sortOrder,
          created_at: new Date().toISOString(),
        };
        setLocalServices((prev) => [...prev, tempService]);
        setLocalQuota((prev) => ({ ...prev, current: prev.current + 1 }));
      }

      // Fire API call in background
      try {
        if (existing) {
          await api.del(`/status-page/services/${existing.id}`);
        } else {
          const sortOrder =
            prevServices.length > 0
              ? Math.max(...prevServices.map((s) => s.sort_order)) + 1
              : 0;
          await api.post("/status-page/services", {
            check_uuid: check.uuid,
            display_name: check.name || check.uuid,
            sort_order: sortOrder,
          });
        }
        // Sync real data from server (silently, to get real IDs)
        skipNextPropSync.current = false;
        onServicesChanged();
      } catch (err) {
        // Revert optimistic update
        setLocalServices(prevServices);
        setLocalQuota(prevQuota);
        setError(
          err instanceof Error ? err.message : "Failed to update service"
        );
      }
    },
    [serviceByUUID, localServices, localQuota, onServicesChanged]
  );

  // ── Save display name ──

  const handleSaveDisplayName = useCallback(
    async (serviceId: string) => {
      const svc = localServices.find((s) => s.id === serviceId);
      if (!svc) return;
      setSavingName(true);
      setError("");
      try {
        await api.put(`/status-page/services/${serviceId}`, {
          display_name: editingDisplayName.trim() || svc.display_name,
          sort_order: svc.sort_order,
        });
        setEditingServiceId(null);
        onServicesChanged();
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to update display name"
        );
      } finally {
        setSavingName(false);
      }
    },
    [localServices, editingDisplayName, onServicesChanged]
  );

  // ── Reorder ──

  const handleReorder = useCallback(
    async (serviceId: string, direction: "up" | "down") => {
      const sorted = [...localServices].sort((a, b) => a.sort_order - b.sort_order);
      const idx = sorted.findIndex((s) => s.id === serviceId);
      if (idx < 0) return;
      const swapIdx = direction === "up" ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= sorted.length) return;

      const aOrder = sorted[idx].sort_order;
      const bOrder = sorted[swapIdx].sort_order;
      const newA =
        bOrder !== aOrder ? bOrder : direction === "up" ? aOrder - 1 : aOrder + 1;
      const newB =
        bOrder !== aOrder ? aOrder : direction === "up" ? bOrder + 1 : bOrder - 1;

      setError("");
      try {
        await api.put("/status-page/services/reorder", {
          order: [
            { id: sorted[idx].id, sort_order: newA },
            { id: sorted[swapIdx].id, sort_order: newB },
          ],
        });
        onServicesChanged();
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to reorder services"
        );
        onServicesChanged(); // revert
      }
    },
    [localServices, onServicesChanged]
  );

  // ── Expand/Collapse All ──

  const allProjectNames = useMemo(
    () => Array.from(grouped.keys()),
    [grouped]
  );

  const allCollapsed = allProjectNames.length > 0 && allProjectNames.every((p) => collapsedProjects.has(p));

  const handleToggleAll = useCallback(() => {
    if (allCollapsed) {
      // Expand all
      setCollapsedProjects(new Set());
    } else {
      // Collapse all
      setCollapsedProjects(new Set(allProjectNames));
    }
  }, [allCollapsed, allProjectNames]);

  // ── Get ordered position info for a service ──

  const sortedServices = useMemo(
    () => [...localServices].sort((a, b) => a.sort_order - b.sort_order),
    [localServices]
  );

  const serviceOrderIndex = useMemo(() => {
    const m = new Map<string, number>();
    sortedServices.forEach((s, i) => m.set(s.id, i));
    return m;
  }, [sortedServices]);

  // ── Render ─────────────────────────────────────────────────────────────────

  if (featureUnavailable) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">
        Status page services are not available on your current plan.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {/* Quota + search bar + expand/collapse all */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {formatQuota(localQuota.current, localQuota.limit)} services selected
        </span>
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="Search checks..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full border rounded px-3 py-1.5 text-sm bg-background"
          />
        </div>
        {allProjectNames.length > 0 && (
          <button
            type="button"
            onClick={handleToggleAll}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap px-2 py-1 rounded border hover:bg-muted"
          >
            {allCollapsed ? "Expand All" : "Collapse All"}
          </button>
        )}
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {/* Grouped check list */}
      {checkDefs.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          No check definitions found. Create checks first to add them to the
          status page.
        </p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          No checks match your search.
        </p>
      ) : (
        <div className="border rounded-lg overflow-hidden divide-y divide-border">
          {Array.from(grouped.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([project, groupMap]) => {
              const isCollapsed = collapsedProjects.has(project);

              // Count how many checks in this project are on the status page
              const projectChecks = Array.from(groupMap.values()).flat();
              const selectedCount = projectChecks.filter((c) =>
                serviceByUUID.has(c.uuid)
              ).length;

              return (
                <div key={project}>
                  {/* Project header — collapsible */}
                  <button
                    type="button"
                    onClick={() => toggleProject(project)}
                    className="w-full flex items-center gap-2 px-3 py-2 bg-muted/50 hover:bg-muted/80 transition-colors text-left"
                  >
                    <span className="text-xs text-muted-foreground select-none">
                      {isCollapsed ? "▶" : "▼"}
                    </span>
                    <span className="text-sm font-semibold flex-1 truncate">
                      {project}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {selectedCount > 0 && (
                        <span className="text-primary font-medium">
                          {selectedCount} selected
                        </span>
                      )}
                      {selectedCount > 0 && " · "}
                      {projectChecks.length} check
                      {projectChecks.length !== 1 ? "s" : ""}
                    </span>
                  </button>

                  {!isCollapsed && (
                    <div className="divide-y divide-border">
                      {Array.from(groupMap.entries())
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([group, checks]) => (
                          <div key={`${project}-${group}`}>
                            {/* Group sub-header (only if multiple groups or meaningful name) */}
                            {(groupMap.size > 1 ||
                              group !== "(No Group)") && (
                              <div className="px-3 py-1 text-[11px] font-medium text-muted-foreground/70 bg-muted/25 border-b border-border">
                                {group}
                              </div>
                            )}

                            {/* Checks in this group */}
                            {checks.map((check) => {
                              const svc = serviceByUUID.get(check.uuid);
                              const isSelected = !!svc;
                              const disableAdd =
                                !isSelected && atQuotaLimit;
                              const target = checkTarget(check);
                              const orderIdx = svc
                                ? serviceOrderIndex.get(svc.id)
                                : undefined;
                              const isFirst = orderIdx === 0;
                              const isLast =
                                orderIdx === sortedServices.length - 1;

                              return (
                                <div
                                  key={check.uuid}
                                  className={`flex items-start gap-3 px-3 py-2.5 transition-colors ${
                                    isSelected
                                      ? "bg-primary/5"
                                      : "hover:bg-muted/30"
                                  }`}
                                >
                                  {/* Checkbox */}
                                  <label className="flex items-center pt-0.5 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      disabled={disableAdd}
                                      onChange={() => handleToggle(check)}
                                      className="rounded border-input h-4 w-4 text-primary focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                    />
                                    <span className="sr-only">Select {check.name || check.uuid}</span>
                                  </label>

                                  {/* Check info + display name */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium truncate">
                                        {check.name || check.uuid}
                                      </span>
                                      <span
                                        className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase shrink-0 ${checkTypeBadge(check.type)}`}
                                      >
                                        {check.type}
                                      </span>
                                    </div>
                                    {target && (
                                      <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                                        {target}
                                      </p>
                                    )}

                                    {/* Display name editor (only for selected services) */}
                                    {isSelected && svc && (
                                      <div className="mt-1.5">
                                        {editingServiceId === svc.id ? (
                                          <div className="flex gap-1.5 items-center">
                                            <input
                                              type="text"
                                              value={editingDisplayName}
                                              onChange={(e) =>
                                                setEditingDisplayName(
                                                  e.target.value
                                                )
                                              }
                                              className="border rounded px-2 py-0.5 text-xs bg-background flex-1 min-w-0"
                                              onKeyDown={(e) => {
                                                if (e.key === "Enter")
                                                  handleSaveDisplayName(svc.id);
                                                if (e.key === "Escape")
                                                  setEditingServiceId(null);
                                              }}
                                            />
                                            <button
                                              onClick={() =>
                                                handleSaveDisplayName(svc.id)
                                              }
                                              disabled={savingName}
                                              className="text-[11px] px-1.5 py-0.5 rounded bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
                                            >
                                              {savingName ? "..." : "Save"}
                                            </button>
                                            <button
                                              onClick={() =>
                                                setEditingServiceId(null)
                                              }
                                              className="text-[11px] px-1.5 py-0.5 rounded border hover:bg-muted"
                                            >
                                              Cancel
                                            </button>
                                          </div>
                                        ) : (
                                          <button
                                            onClick={() => {
                                              setEditingServiceId(svc.id);
                                              setEditingDisplayName(
                                                svc.display_name
                                              );
                                            }}
                                            className="text-xs text-muted-foreground hover:text-primary transition-colors truncate text-left"
                                            title="Click to edit display name"
                                          >
                                            Display name:{" "}
                                            <span className="font-medium text-foreground">
                                              {svc.display_name}
                                            </span>
                                            <span className="ml-1 text-muted-foreground/50">
                                              (edit)
                                            </span>
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  {/* Reorder controls (only for selected services) */}
                                  {isSelected && svc && sortedServices.length > 1 && (
                                    <div className="flex flex-col gap-0.5 shrink-0 pt-0.5">
                                      <button
                                        onClick={() =>
                                          handleReorder(svc.id, "up")
                                        }
                                        disabled={isFirst}
                                        title="Move up on status page"
                                        className="text-muted-foreground hover:text-foreground disabled:opacity-30 text-xs leading-none p-0.5"
                                      >
                                        ▲
                                      </button>
                                      <button
                                        onClick={() =>
                                          handleReorder(svc.id, "down")
                                        }
                                        disabled={isLast}
                                        title="Move down on status page"
                                        className="text-muted-foreground hover:text-foreground disabled:opacity-30 text-xs leading-none p-0.5"
                                      >
                                        ▼
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}

      {/* Quota warning */}
      {atQuotaLimit && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          Service limit reached ({localQuota.current}/{localQuota.limit}). Uncheck a
          service or upgrade your plan to add more.
        </p>
      )}
    </div>
  );
}
